import React from 'react';
import { useTranslation } from 'react-i18next';
import { getThemeColors } from '../../lib/theme';
import { matchFieldLabel, type MatchField } from '../../lib/matching';

/** Gewicht kompakt in der UI-Sprache: 1 → „1", 2.5 → „2,5" (DE/PL) bzw. „2.5" (EN). */
const fmtWeight = (w: number, lng: string) => new Intl.NumberFormat(lng, { maximumFractionDigits: 2 }).format(w);

/**
 * Feld-für-Feld-Aufschlüsselung eines Match-Scores.
 * Grün = erfüllt (1), Gelb = teilweise (0<x<1), Grau = nicht erfüllt (0).
 *
 * Liefert das Backend die Gewichte aus den Admin-Einstellungen mit
 * (`fields[].weight`), steht je Feld „×Gewicht" daneben; Felder mit
 * Gewicht 0 sind als „ignoriert" ausgegraut. Der Gesamtscore ist der
 * gewichtete Durchschnitt der Feld-Scores. Felder ohne Wunsch des PB
 * (`neutral`) zählen nicht mit und stehen als „kein Wunsch" ausgegraut da.
 */
export function MatchDetailsPopover({ fields, dark }: { fields: MatchField[]; dark: boolean }) {
  const t = getThemeColors(dark);
  const { t: tr, i18n } = useTranslation();

  const dot = (score: number) =>
    score >= 1 ? '#12b76a' : score > 0 ? '#f79009' : (dark ? '#3a4356' : '#d0d5dd');

  const weighted = fields.length > 0 && fields.every(f => f.weight != null);

  return (
    <div style={{
      background: t.card, border: `1px solid ${t.border}`, borderRadius: 10,
      boxShadow: dark ? '0 8px 24px rgba(0,0,0,0.45)' : '0 8px 24px -6px rgba(16,24,40,0.18)',
      padding: '10px 12px', width: 280, maxHeight: 360, overflowY: 'auto',
    }}>
      <div style={{ fontSize: 10.5, fontWeight: 700, color: t.muted, textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8, display: 'flex', gap: 8 }}>
        <span style={{ flex: 1 }}>{tr('matching.breakdown.title')}</span>
        {weighted && <span>{tr('matching.breakdown.weight')}</span>}
      </div>
      {fields.map(f => {
        const ignored = weighted && (f.weight ?? 0) <= 0;
        const neutral = !ignored && f.neutral === true;
        return (
          <div key={f.name} data-field={f.name} data-ignored={ignored ? 'true' : undefined} data-neutral={neutral ? 'true' : undefined} style={{ padding: '3px 0', opacity: ignored || neutral ? 0.5 : 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: neutral ? dot(0) : dot(f.score), flexShrink: 0 }} />
              <span style={{ fontSize: 12, color: t.text, flex: 1 }}>{matchFieldLabel(f.name, tr)}</span>
              <span style={{ fontSize: 11, color: t.muted, fontVariantNumeric: 'tabular-nums' }}>
                {neutral ? tr('matching.breakdown.neutral') : `${Math.round(f.score * 100)}%`}
              </span>
              {weighted && (
                <span data-weight style={{ fontSize: 11, color: ignored ? t.muted : t.text, fontWeight: 600, minWidth: 52, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                  {ignored ? tr('matching.breakdown.ignored') : `×${fmtWeight(f.weight!, i18n.language || 'de')}`}
                </span>
              )}
            </div>
            {/* Detail-Felder (z.B. Wohnraum): Werte beider Seiten */}
            {(f.pb || f.bk) && (
              <div style={{ fontSize: 11, color: t.muted, paddingLeft: 16, marginTop: 1, lineHeight: 1.4 }}>
                <div>{tr('matching.breakdown.pb_offers')}: <b style={{ color: t.text }}>{f.pb?.length ? f.pb.join(', ') : '—'}</b></div>
                <div>{tr('matching.breakdown.bk_wants')}: <b style={{ color: t.text }}>{f.bk?.length ? f.bk.join(', ') : '—'}</b></div>
              </div>
            )}
          </div>
        );
      })}
      {weighted && (
        <div style={{ marginTop: 8, paddingTop: 6, borderTop: `1px solid ${t.border}`, fontSize: 11, color: t.muted, lineHeight: 1.4 }}>
          {tr('matching.breakdown.weighted_hint')}
        </div>
      )}
    </div>
  );
}
